import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { cloneDeep } from 'lodash';
import { catchError, concatMap, EMPTY, exhaustMap, filter, iif, map, of, switchMap, tap } from 'rxjs';

import { DetailType } from '@ninety/platform/models/_shared/detail-type.enum';
import { OrdinalOrUserOrdinalUpdate } from '@ninety/platform/models/_shared/ordinal-or-user-ordinal-update';
import type { CascadingMessagesSortField } from '@ninety/platform/models/cascading-messages/cascading-message-sort';
import { Scopes } from '@ninety/platform/models/entitlements/entitlements-model';
import { ItemType } from '@ninety/platform/models/enums/item-type';
import { FromLinkedItem, LinkedItemTypeEnum } from '@ninety/platform/models/linked-items/linked-item-type-enum';
import { CascadingMessage, CmReceivedRealtimeMessage } from '@ninety/platform/models/meetings/cascading-message';
import { HeadlineMessageType } from '@ninety/platform/models/meetings/headline-message-type';
import type { ListSortMessage } from '@ninety/platform/models/meetings/list-sort-message';
import { LocalStorageService, extractValueFromStore } from '@ninety/platform/services';
import {
  DetailViewActions,
  selectFeatureEnabled,
  appActions,
  RealTimeActions,
  TeamSelectors,
  selectCurrentUser,
  UserSelectors,
  selectCurrentUserId,
  selectLanguage,
} from '@ninety/platform/state';
import { ConfirmDialogComponent, ConfirmDialogData } from '@ninety/ui/core';
import { CreateDialogService } from '@ninety/web/pages/layouts/services/create-dialog.service';
import { MeetingService } from '@ninety/web/pages/meeting/_shared/services/meeting.service';
import { MeetingsStateSelectors } from '@ninety/web/pages/meeting/_state/meetings.selectors';

import { HeadlineService } from '../../_shared/services/headline.service';
import { HeadlinesStateActions } from '../headlines/headlines-state.actions';
import * as headlinesSelectors from '../headlines/headlines-state.selectors';

import { CascadingMessagesStateActions } from './cascading-messages-state.actions';
import { CascadingMessageStateKey } from './cascading-messages-state.model';
import * as cascadingMessagesSelectors from './cascading-messages-state.selectors';

@Injectable()
export class CascadingMessagesStateEffects {
  private readonly actions$ = inject(Actions);
  private readonly headlineService = inject(HeadlineService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly store = inject(Store);
  private readonly createDialogService = inject(CreateDialogService);
  private readonly meetingService = inject(MeetingService);
  private readonly dialog = inject(MatDialog);

  static readonly source = 'CascadingMessagesStateEffects';

  paginationChange$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CascadingMessagesStateActions.paginationChange),
        tap(({ size }) => this.localStorageService.set(`${CascadingMessageStateKey}.pagination.size`, size))
      ),
    { dispatch: false }
  );

  sortChange$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CascadingMessagesStateActions.sortChange),
        tap(({ sort }) => {
          if (sort.direction) {
            //direction is null when sort cycle is complete
            this.localStorageService.set(`${CascadingMessageStateKey}.sort`, sort);
          } else {
            this.localStorageService.delete(`${CascadingMessageStateKey}.sort`);
          }
        })
      ),
    { dispatch: false }
  );

  clearSort$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CascadingMessagesStateActions.clearSort),
        tap(_ => this.localStorageService.delete(`${CascadingMessageStateKey}.sort`))
      ),
    { dispatch: false }
  );

  broadcastOnSortChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CascadingMessagesStateActions.sortChange),
      filter(_ => this.headlineService.shouldBroadcast),
      concatLatestFrom(() => [
        this.store.select(cascadingMessagesSelectors.selectFilterByTeamId),
        this.store.select(MeetingsStateSelectors.selectCurrentMeetingPresenterUserId),
        this.store.select(selectCurrentUser),
      ]),
      filter(([, , currentMeetingPresenterUserId, currentUser]) => {
        // Only send broadcast messages if the current user is the presenter
        // We don't want to send messages if the user is not presenter
        return !currentUser?.isObserver && currentMeetingPresenterUserId === currentUser._id;
      }),
      map(([{ sort }, teamId]) =>
        CascadingMessagesStateActions.broadcastMessage({
          message: {
            messageType: HeadlineMessageType.sort,
            document: {
              listType: 'cascaded',
              field: sort.field as string,
              currentTeamId: teamId,
              sortDirection: sort.direction,
              screen: 'headlines',
              //TODO NEXT: handle conclude when refactored. maybe even skip this for conclude as lists are combined,
              //because lists are combined on conclude and we save sort in local storage
            },
          },
        })
      )
    )
  );

  openCreateDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CascadingMessagesStateActions.openCreateDialog),
        switchMap(() => this.createDialogService.open({ itemType: ItemType.cascadedMessage }))
      ),
    { dispatch: false }
  );

  get$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CascadingMessagesStateActions.get,
        CascadingMessagesStateActions.getInBackground,
        CascadingMessagesStateActions.filterByTeam,
        CascadingMessagesStateActions.sortChange,
        CascadingMessagesStateActions.paginationChange,
        CascadingMessagesStateActions.showArchived,
        CascadingMessagesStateActions.search,
        //archive is done with a headlines effect for cms as well
        HeadlinesStateActions.archiveAllCompletedSuccess
      ),
      concatLatestFrom(() => [this.store.select(selectFeatureEnabled(Scopes.cascadingMessages))]),
      filter(([_, enabled]) => enabled),
      concatLatestFrom(() => [this.store.select(cascadingMessagesSelectors.selectGetCascadingMessageQueryParams)]),
      switchMap(([_, params]) =>
        this.headlineService.getCascadingMessagesByTeamId(params).pipe(
          map(cascadingMessages =>
            CascadingMessagesStateActions.getSuccess({
              cascadingMessages,
              meeting: cloneDeep(this.meetingService.currentMeeting),
            })
          ),
          catchError((error: unknown) => of(CascadingMessagesStateActions.getFailed({ error })))
        )
      )
    )
  );

  setSelectedCascadingMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CascadingMessagesStateActions.getSuccess),
      concatLatestFrom(() => [this.store.select(cascadingMessagesSelectors.selectCascadingMessageIdFromDetailRoute)]),
      filter(([_, cascadingMessageId]) => !!cascadingMessageId),
      map(([_, cascadingMessageId]) => CascadingMessagesStateActions.setSelected({ selectedId: cascadingMessageId }))
    )
  );

  setSelectedCascadingMessageOnDetailOpen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DetailViewActions.opened),
      filter(({ config }) => config.pathParts[0] === DetailType.cascadingMessage),
      map(({ config }) => CascadingMessagesStateActions.setSelected({ selectedId: config.pathParts[1] }))
    )
  );

  openInDetailView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CascadingMessagesStateActions.openInDetailView),
      map(action =>
        DetailViewActions.opened({
          config: { pathParts: [DetailType.cascadingMessage, action.cascadingMessageId] },
          source: 'Cascading Message V2 Selected',
        })
      )
    )
  );

  clearSelectedOnClosed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DetailViewActions.closed),
      filter(({ itemType }) => this.isCascadingMessageAction(itemType)),
      map(() => CascadingMessagesStateActions.clearSelected())
    )
  );

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CascadingMessagesStateActions.update),
      concatMap(({ update }) =>
        this.headlineService.updateHeadline(update.id as string, update.changes).pipe(
          map(_ => CascadingMessagesStateActions.updateSuccess({ update })),
          catchError((error: unknown) => of(CascadingMessagesStateActions.updateFailed({ error })))
        )
      )
    )
  );

  //TODO NEXT: remove/refactor when meeting is moved to store
  updateCascadedMessagesDocsInMeeting$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CascadingMessagesStateActions.updateSuccess, CascadingMessagesStateActions.setUserSuccess),
        filter(_ => this.meetingService.currentMeeting?.inProgress),
        tap(({ update }) => {
          const cascadingMessageDocs = [...(this.meetingService.currentMeeting.cascadingMessageDocs || [])];
          const indexCm = cascadingMessageDocs.findIndex(h => h._id === update.id);
          if (indexCm >= 0) {
            cascadingMessageDocs[indexCm] = { ...cascadingMessageDocs[indexCm], ...update.changes };
            this.meetingService.setCurrentMeeting({ ...this.meetingService.currentMeeting, cascadingMessageDocs });
          }

          const doneHeadlineDocs = [...(this.meetingService.currentMeeting.doneHeadlineDocs || [])];
          const indexH = doneHeadlineDocs.findIndex(h => h._id === update.id);
          if (indexH >= 0) {
            doneHeadlineDocs[indexH] = { ...doneHeadlineDocs[indexH], ...update.changes };
            this.meetingService.setCurrentMeeting({ ...this.meetingService.currentMeeting, doneHeadlineDocs });
          }
        })
      ),
    { dispatch: false }
  );

  broadcastOnUpdateSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CascadingMessagesStateActions.updateSuccess),
      filter(_ => this.headlineService.shouldBroadcast),
      map(({ update }) =>
        CascadingMessagesStateActions.broadcastMessage({
          message: {
            messageType: HeadlineMessageType.headline,
            document: { ...update.changes, _id: update.id as string },
            isCascadingMessage: true,
          },
        })
      )
    )
  );

  setTeam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CascadingMessagesStateActions.setTeam),
      concatMap(({ id, teamId }) =>
        this.headlineService.updateHeadline(id, { teamId }).pipe(
          map(_ => CascadingMessagesStateActions.setTeamSuccess({ id, teamId })),
          catchError((error: unknown) => of(CascadingMessagesStateActions.setTeamFailed({ error })))
        )
      )
    )
  );

  broadcastOnSetTeamSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CascadingMessagesStateActions.setTeamSuccess),
      filter(_ => this.headlineService.shouldBroadcast),
      map(({ id, teamId }) =>
        CascadingMessagesStateActions.broadcastMessage({
          message: {
            messageType: HeadlineMessageType.headline,
            document: { teamId, _id: id },
            isCascadingMessage: true,
          },
        })
      )
    )
  );

  setUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CascadingMessagesStateActions.setUser),
      concatMap(({ id, ownedByUserId }) =>
        this.headlineService.updateHeadline(id, { ownedByUserId }).pipe(
          map(update =>
            CascadingMessagesStateActions.setUserSuccess({
              update: { id, changes: { ownedByUserId: update.ownedByUserId, ownedByUser: update.ownedByUser } },
            })
          ),
          catchError((error: unknown) => of(CascadingMessagesStateActions.setUserFailed({ error })))
        )
      )
    )
  );

  broadcastOnSetUserSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CascadingMessagesStateActions.setUserSuccess),
      filter(_ => this.headlineService.shouldBroadcast),
      map(({ update }) =>
        CascadingMessagesStateActions.broadcastMessage({
          message: {
            messageType: HeadlineMessageType.headline,
            document: { ...update.changes, _id: update.id as string },
            isCascadingMessage: true,
          },
        })
      )
    )
  );

  setArchived$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CascadingMessagesStateActions.setArchived),
      concatMap(({ id, archived }) =>
        this.headlineService.setArchived(id, archived).pipe(
          map(_ => CascadingMessagesStateActions.setArchivedSuccess({ id, archived })),
          catchError((error: unknown) => of(CascadingMessagesStateActions.setArchivedFailed({ error })))
        )
      )
    )
  );

  broadcastSetArchivedSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CascadingMessagesStateActions.setArchivedSuccess),
      map(({ id, archived }) =>
        CascadingMessagesStateActions.broadcastMessage({
          message: {
            messageType: archived ? HeadlineMessageType.archive : HeadlineMessageType.unarchive,
            document: id,
            isCascadingMessage: true,
          },
        })
      )
    )
  );

  setCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CascadingMessagesStateActions.setCompleted),
      concatMap(({ id, completed }) =>
        this.headlineService.setCompleted(id, completed, this.meetingService.currentMeeting?._id).pipe(
          map(update => {
            if (!update.isDone) {
              //remove isDone meta fields by setting them to undefined
              update.completedDate = undefined;
              update.completedBy = undefined;
              update.completedInMeeting = undefined;
            }
            return CascadingMessagesStateActions.setCompletedSuccess({ update: { id, changes: update } });
          }),
          catchError((error: unknown) => of(CascadingMessagesStateActions.setCompletedFailed({ error })))
        )
      )
    )
  );

  setCompletedInMeeting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CascadingMessagesStateActions.setCompletedSuccess),
      filter(_ => this.meetingService.currentMeeting?.inProgress),
      concatMap(({ update }) =>
        iif(
          () => update.changes.isDone,
          this.meetingService.addDoneHeadline(update.id as string).pipe(map(_ => update)),
          this.meetingService.removeDoneHeadline(update.id as string).pipe(map(_ => update))
        ).pipe(
          map(update => CascadingMessagesStateActions.setCompletedInMeetingSuccess({ update })),
          catchError((error: unknown) => of(CascadingMessagesStateActions.setCompletedInMeetingFailed({ error })))
        )
      )
    )
  );

  broadcastSetCompletedInMeeting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CascadingMessagesStateActions.setCompletedInMeetingSuccess),
      filter(_ => this.headlineService.shouldBroadcast),
      map(({ update }) =>
        CascadingMessagesStateActions.broadcastMessage({
          message: {
            messageType: HeadlineMessageType.headline,
            document: { ...update.changes, _id: update.id as string },
            isCascadingMessage: true,
          },
        })
      )
    )
  );

  deleteConfirm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CascadingMessagesStateActions.deleteConfirm),
      concatLatestFrom(() => this.store.select(selectLanguage)),
      exhaustMap(([{ id }, language]) =>
        this.dialog
          .open<ConfirmDialogComponent, ConfirmDialogData>(ConfirmDialogComponent, {
            data: ConfirmDialogData.WarningDialog({
              title: `Delete ${language.cascadingMessage.item}`,
              message: `Are you sure you want to delete this ${language.cascadingMessage.item}?`,
              additionalMessage: `This will remove the ${language.cascadingMessage.item} and all its associated content. Items in the trash will be permanently deleted after 90 days.`,
            }),
          })
          .afterClosed()
          .pipe(
            map((confirmed: boolean) => (confirmed ? CascadingMessagesStateActions.delete({ id }) : appActions.noop()))
          )
      )
    )
  );

  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CascadingMessagesStateActions.delete),
      concatMap(({ id }) =>
        this.headlineService.deleteHeadline(id).pipe(
          map(_ => CascadingMessagesStateActions.deleteSuccess({ id })),
          catchError((error: unknown) => of(CascadingMessagesStateActions.deleteFailed({ error })))
        )
      )
    )
  );

  inMeetingRemoveCascadedMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CascadingMessagesStateActions.deleteSuccess),
        filter(_ => this.meetingService.currentMeeting?.inProgress),
        concatMap(({ id }) =>
          this.meetingService.removeCascadingMessage(id).pipe(
            map(() => CascadingMessagesStateActions.removeCascadedMessageSuccess()),
            catchError(() => of(CascadingMessagesStateActions.removeCascadedMessageFailure()))
          )
        )
      ),
    { dispatch: false }
  );

  //you can complete a CM in detail view, so we need to remove it from doneHeadlines as well
  //TODO NEXT: discuss feature
  inMeetingRemoveDoneHeadline$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CascadingMessagesStateActions.deleteSuccess),
        filter(_ => this.meetingService.currentMeeting?.inProgress),
        filter(({ id }) => this.meetingService.currentMeeting?.doneHeadlines.includes(id)),
        concatMap(({ id }) =>
          this.meetingService.removeDoneHeadline(id).pipe(
            map(() => CascadingMessagesStateActions.removeDoneHeadlineSuccess()),
            catchError(() => of(CascadingMessagesStateActions.removeDoneHeadlineFailure()))
          )
        )
      ),
    { dispatch: false }
  );

  broadcastOnDeleteSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CascadingMessagesStateActions.deleteSuccess),
      filter(_ => this.headlineService.shouldBroadcast),
      map(({ id }) =>
        CascadingMessagesStateActions.broadcastMessage({
          message: {
            messageType: HeadlineMessageType.delete,
            document: id,
            isCascadingMessage: true,
          },
        })
      )
    )
  );

  closeDetailIfOpened$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CascadingMessagesStateActions.deleteLocal),
      concatLatestFrom(() => [this.store.select(cascadingMessagesSelectors.selectSelectedId)]),
      filter(([{ id }, selectedId]) => id === selectedId),
      map(_ => DetailViewActions.close())
    )
  );

  closeDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CascadingMessagesStateActions.delete,
        CascadingMessagesStateActions.setArchived,
        CascadingMessagesStateActions.clearSelected,
        CascadingMessagesStateActions.setTeam
      ),
      map(() => DetailViewActions.close())
    )
  );

  private isCascadingMessageAction(itemType: DetailType) {
    return itemType === DetailType.cascadingMessage;
  }

  cascade$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CascadingMessagesStateActions.cascade),
        switchMap(({ cascadingMessage }) =>
          this.createDialogService.open({
            item: { ...cascadingMessage, isCascadedMessage: true },
            itemType: ItemType.cascadedMessage,
          })
        )
      ),
    {
      dispatch: false,
    }
  );

  createIssue$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CascadingMessagesStateActions.createIssue),
        concatLatestFrom(({ cascadingMessage }) => [
          this.store.select(cascadingMessagesSelectors.selectFilterByTeamId),
          this.store.select(UserSelectors.selectUserById(cascadingMessage.ownedByUserId)),
        ]),
        concatMap(([{ cascadingMessage }, teamId, owner]) => {
          let defaultUser = owner._id;
          if (!owner.teams.find(team => team.teamId === teamId)) {
            defaultUser = null;
          }
          const createdFrom: FromLinkedItem = {
            id: cascadingMessage._id,
            type: LinkedItemTypeEnum.cascadingMessage,
          };
          return this.createDialogService.open({
            item: {
              title: cascadingMessage.title,
              description: cascadingMessage.description,
              comments: cascadingMessage.comments,
              attachments: cascadingMessage.attachments,
              userId: defaultUser,
            },
            itemType: ItemType.issue,
            ...{ createdFrom },
          });
        })
      ),
    {
      dispatch: false,
    }
  );

  createTodo$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CascadingMessagesStateActions.createTodo),
        concatMap(({ cascadingMessage }) => {
          const createdFrom: FromLinkedItem = {
            id: cascadingMessage._id,
            type: LinkedItemTypeEnum.cascadingMessage,
          };
          return this.createDialogService.open({
            item: {
              title: cascadingMessage.title,
              description: cascadingMessage.description,
              comments: cascadingMessage.comments,
              attachments: cascadingMessage.attachments,
              userId: cascadingMessage.ownedByUserId,
            },
            itemType: ItemType.todo,
            ...{ createdFrom },
          });
        })
      ),
    {
      dispatch: false,
    }
  );

  createHeadline$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CascadingMessagesStateActions.createHeadline),
        concatMap(({ cascadingMessage }) => {
          const createdFrom: FromLinkedItem = {
            id: cascadingMessage._id,
            type: LinkedItemTypeEnum.cascadingMessage,
          };
          return this.createDialogService.open({
            item: {
              title: cascadingMessage.title,
              description: cascadingMessage.description,
              comments: cascadingMessage.comments,
              attachments: cascadingMessage.attachments,
              userId: cascadingMessage.ownedByUserId,
            },
            itemType: ItemType.headline,
            ...{ createdFrom },
          });
        })
      ),
    {
      dispatch: false,
    }
  );

  createRock$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CascadingMessagesStateActions.createRock),
        concatMap(({ cascadingMessage }) => {
          const createdFrom: FromLinkedItem = {
            id: cascadingMessage._id,
            type: LinkedItemTypeEnum.cascadingMessage,
          };
          return this.createDialogService.open({
            item: {
              title: cascadingMessage.title,
              description: cascadingMessage.description,
              comments: cascadingMessage.comments,
              attachments: cascadingMessage.attachments,
              userId: cascadingMessage.ownedByUserId,
            },
            itemType: ItemType.rock,
            ...{ createdFrom },
          });
        })
      ),
    {
      dispatch: false,
    }
  );

  updateOrdinals$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CascadingMessagesStateActions.updateOrdinals),
      concatLatestFrom(() => [
        this.store.select(cascadingMessagesSelectors.selectCascadingMessages),
        this.store.select(cascadingMessagesSelectors.selectPagination),
        this.store.select(cascadingMessagesSelectors.selectFilterBy),
        this.store.select(cascadingMessagesSelectors.selectSort),
      ]),
      map(([{ previousIndex, currentIndex }, cms, pagination, filterBy, sort]) => {
        const start = Math.min(previousIndex, currentIndex) + pagination.index * pagination.size;
        const stop = Math.max(previousIndex, currentIndex) + pagination.index * pagination.size;

        const cmsWithNewOrdinals = cms.filter(t => t.ordinal >= start && t.ordinal <= stop);
        this.headlineService
          .updateOrdinals(
            cmsWithNewOrdinals.map(
              (cm: CascadingMessage) => new OrdinalOrUserOrdinalUpdate(cm._id, cm.ordinal, 'ordinal')
            ),
            'ordinal',
            filterBy.teamId,
            false,
            filterBy.inMeetingId,
            sort.field,
            sort.direction
          )
          .subscribe();

        return CascadingMessagesStateActions.clearSort();
      })
    )
  );

  broadcastMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CascadingMessagesStateActions.broadcastMessage),
      map(({ message }) =>
        RealTimeActions.sendMessage({
          channelId: this.headlineService.channelId,
          message: cloneDeep(message),
        })
      )
    )
  );

  handleRealTimeMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RealTimeActions.messageReceived),
      filter(({ channelId }) => channelId === this.headlineService.channelId),
      concatLatestFrom(() => [this.store.select(headlinesSelectors.selectInMeetingOnHeadlines)]),
      filter(([_, inMeetingOnHeadlines]) => inMeetingOnHeadlines),
      map(([{ message }]) => message as CmReceivedRealtimeMessage),
      switchMap(message => {
        switch (message.messageType) {
          case HeadlineMessageType.new:
            if (message.isCascadingMessage) {
              // currently you can't create a CM for the meeting team
              return EMPTY;
            }
          case HeadlineMessageType.delete:
            if (message.isCascadingMessage) {
              return of(CascadingMessagesStateActions.deleteCMMessageReceived({ message }));
            }
          case HeadlineMessageType.archive:
            if (message.isCascadingMessage) {
              return of(CascadingMessagesStateActions.archiveCMMessageReceived({ message }));
            }
          case HeadlineMessageType.unarchive:
            if (message.isCascadingMessage) {
              return of(CascadingMessagesStateActions.unArchiveCMMessageReceived({ message }));
            }
          case HeadlineMessageType.headline:
            if (message.isCascadingMessage) {
              return of(CascadingMessagesStateActions.updateCMMessageReceived({ message }));
            }
          case HeadlineMessageType.sort:
            if ((message.document as ListSortMessage).listType === 'cascaded') {
              return of(CascadingMessagesStateActions.sortCmsMessageReceived({ message }));
            }

          default:
            return EMPTY;
        }
      })
    )
  );

  handleUpdateCmMessageReceived$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CascadingMessagesStateActions.updateCMMessageReceived),
      map(({ message }) => {
        const selectedTeamId = extractValueFromStore(this.store, TeamSelectors.selectFilterBarTeamId);
        const update = message.document as Partial<CascadingMessage>;
        if (update.teamId && update.teamId !== selectedTeamId) {
          return CascadingMessagesStateActions.deleteLocal({ id: update._id });
        } else {
          return CascadingMessagesStateActions.updateLocal({ update: { id: update._id, changes: update } });
        }
      })
    )
  );

  handleDeleteCmMessageReceived$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CascadingMessagesStateActions.deleteCMMessageReceived),
      map(({ message }) => {
        return CascadingMessagesStateActions.deleteLocal({ id: message.document as string });
      })
    )
  );

  handleArchiveCmMessageReceived$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CascadingMessagesStateActions.archiveCMMessageReceived),
      filter(_ => !extractValueFromStore(this.store, cascadingMessagesSelectors.selectFilterByArchived)),
      map(({ message }) => {
        return CascadingMessagesStateActions.deleteLocal({ id: message.document as string });
      })
    )
  );

  handleUnArchiveCmMessageReceived$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CascadingMessagesStateActions.unArchiveCMMessageReceived),
      filter(_ => extractValueFromStore(this.store, cascadingMessagesSelectors.selectFilterByArchived)),
      map(({ message }) => {
        return CascadingMessagesStateActions.deleteLocal({ id: message.document as string });
      })
    )
  );

  handleSortCmsMessageReceived$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CascadingMessagesStateActions.sortCmsMessageReceived),
      concatLatestFrom(() => [
        this.store.select(selectCurrentUserId),
        this.store.select(MeetingsStateSelectors.selectCurrentMeetingPresenterUserId),
      ]),
      filter(([, currentUserId, presenterUserId]) => {
        // Skip sort change if current user is the presenter
        // just in case a race condition occurs and the presenter is not correctly set
        return currentUserId !== presenterUserId;
      }),
      map(([{ message }]) => {
        return CascadingMessagesStateActions.sortChange({
          sort: {
            field: (message.document as ListSortMessage).field as CascadingMessagesSortField,
            direction: (message.document as ListSortMessage).sortDirection,
          },
        });
      })
    )
  );
}
